import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { deserializeFiltersFromUrl } from '../../../../util/filterUrlSerializer';
import { IDENTIFIER_TYPES } from '../../../../constants/identifierTypes';
import { setAllFilters, clearAllFilters } from '../../../../actions/allFilterActions';
import { setBusinessesDetailsSearchParamsAndLoad } from '../../../../actions/businessActions';
import { ALLOWED_STATUSES_FOR_TICKET_FILTER } from '../../../../constants/accountStatuses';

// NOTE(vlad|SPD-14367|2026-03-05): Module-level flag — survives component remounts
// but resets on page refresh. Prevents clearAllFilters() from running on tab-switch
// remounts, which would reset hasSearched and lose search results.
let hasInitialized = false;

// Exposed for tests only — resets the module-level init guard
export const __resetHasInitialized = () => {
  hasInitialized = false;
};

const urlFiltersToState = (urlFilters) => {
  const {
    simplrTicketNumber,
    widgetId,
    roomId,
    platformTicketId,
    partialMatch,
    ticketStatus,
    sortOrder,
    dateRange,
    businessIdList,
    excludeBusinesses,
    selectedBrandOptions,
  } = urlFilters;

  const state = {};

  // NOTE(vlad|SPD-14347|2026-03-03): Map identifier fields to consolidated { type, value } pair
  if (simplrTicketNumber) {
    state.identifier = { type: IDENTIFIER_TYPES.SIMPLR_TICKET, value: simplrTicketNumber };
  } else if (widgetId) {
    state.identifier = { type: IDENTIFIER_TYPES.WIDGET_ID, value: widgetId };
  } else if (roomId) {
    state.identifier = { type: IDENTIFIER_TYPES.ROOM_ID, value: roomId };
  } else if (platformTicketId) {
    state.identifier = { type: IDENTIFIER_TYPES.PLATFORM_TICKET_ID, value: platformTicketId };
  }

  if (ticketStatus) {
    state.ticketStatus = ticketStatus;
  }

  if (sortOrder) {
    state.sortOrder = sortOrder;
  }

  if (dateRange) {
    state.dateRange = {
      startDate: dateRange.startDate || null,
      endDate: dateRange.endDate || null,
      startTime: dateRange.startTime || null,
      endTime: dateRange.endTime || null,
    };
  }

  if (businessIdList && businessIdList.length > 0) {
    state.businessIds = businessIdList;
  }

  if (excludeBusinesses) {
    state.excludeBusinesses = true;
  }

  if (selectedBrandOptions && selectedBrandOptions.length > 0) {
    state.selectedBrandOptions = selectedBrandOptions;
  }

  // NOTE(vlad|SPD-15655|2026-08-11): Emit partialMatch explicitly (incl. false) so a clean URL
  // clears a stale Redux `true` — SET_ALL_FILTERS merges and would otherwise keep it on
  // (back/forward desync, David's review on #5425). Gate on other keys existing so an empty
  // URL still yields {} and doesn't trip the `Object.keys(...).length > 0` auto-search heuristic.
  if (Object.keys(state).length > 0) {
    state.partialMatch = Boolean(partialMatch);
  }

  return state;
};

export const useFilterUrlSync = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isInitializing = useRef(true);
  const prevSearchRef = useRef(location.search);
  const [isReady, setIsReady] = useState(false);
  const [initialUrlHadFilters, setInitialUrlHadFilters] = useState(false);

  // NOTE(vlad|SPD-14347|2026-03-03): Initialization — load businesses, then URL → Redux
  useEffect(() => {
    const init = async () => {
      const query = new URLSearchParams(location.search);
      const ticketNumber = query.get('ticket') || '';
      await dispatch(setBusinessesDetailsSearchParamsAndLoad(ticketNumber, ALLOWED_STATUSES_FOR_TICKET_FILTER));

      // NOTE(vlad|SPD-14367|2026-03-05): Clear persisted (but un-searched) filter values on mount.
      // Only URL-backed filters (synced on Search click) should survive a refresh.
      // Guard with hasInitialized so tab-switch remounts don't reset hasSearched.
      if (!hasInitialized) {
        dispatch(clearAllFilters());
        hasInitialized = true;
      }

      const urlFilters = deserializeFiltersFromUrl(location.search);
      const stateFromUrl = urlFiltersToState(urlFilters);

      const hadFilters = Object.keys(stateFromUrl).length > 0;
      setInitialUrlHadFilters(hadFilters);

      if (hadFilters) {
        dispatch(setAllFilters(stateFromUrl));
      }

      isInitializing.current = false;
      setIsReady(true);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NOTE(vlad|SPD-14347|2026-03-03): URL → Redux — when URL changes externally (e.g. paste, back/forward)
  useEffect(() => {
    if (isInitializing.current) {
      return;
    }

    if (location.search === prevSearchRef.current) {
      return;
    }

    prevSearchRef.current = location.search;

    const urlFilters = deserializeFiltersFromUrl(location.search);
    const stateFromUrl = urlFiltersToState(urlFilters);

    if (Object.keys(stateFromUrl).length > 0) {
      dispatch(setAllFilters(stateFromUrl));
    }
  }, [location.search, dispatch]);

  return { isReady, initialUrlHadFilters };
};
